import {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from 'react'
import { collection, onSnapshot } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../hooks/auth/use_auth'
import {
  UnreadNotification,
  UnreadMessage,
  AttachmentInfo,
} from '../types/chat'

interface UnreadNotificationsContextValue {
  unreadCount: number
  unreadNotifications: Map<number, UnreadNotification>
  unreadMessages: UnreadMessage[]
  loading: boolean
}

const UnreadNotificationsContext =
  createContext<UnreadNotificationsContextValue | null>(null)

interface UnreadNotificationsProviderProps {
  children: ReactNode
}

/**
 * 未読通知をリアルタイムで監視する Provider
 * アプリケーション全体で一度だけ snapshot を実行し、状態を共有する
 */
export const UnreadNotificationsProvider = ({
  children,
}: UnreadNotificationsProviderProps): JSX.Element => {
  const { user } = useAuth()
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [unreadNotifications, setUnreadNotifications] = useState<
    Map<number, UnreadNotification>
  >(new Map())
  const [unreadMessages, setUnreadMessages] = useState<UnreadMessage[]>([])
  const [loading, setLoading] = useState<boolean>(true)

  useEffect(() => {
    // ユーザーがログインしていない場合は何もしない
    if (!user || !user.kuchikomiEnterpriseId) {
      setUnreadCount(0)
      setUnreadNotifications(new Map())
      setUnreadMessages([])
      setLoading(false)
      return
    }

    const enterpriseId = user.kuchikomiEnterpriseId

    // チャットルーム単位の未読通知リスナー（ヘッダーバッジ用）
    const unsubscribeChatRooms = onSnapshot(
      collection(db, `unreadNotifications/${enterpriseId}/chatRooms`),
      (snapshot) => {
        const notificationsMap = new Map<number, UnreadNotification>()
        let totalUnread = 0

        snapshot.forEach((doc) => {
          const data = doc.data()
          const chatRoomId = parseInt(doc.id, 10)

          const notification: UnreadNotification = {
            chatRoomId,
            unreadCount: data.unreadCount,
            lastMessageAt: data.lastMessageAt.toDate(),
            lastSenderId: data.lastSenderId,
          }

          notificationsMap.set(chatRoomId, notification)
          totalUnread += notification.unreadCount
        })

        setUnreadNotifications(notificationsMap)
        setUnreadCount(totalUnread)
      },
      (error) => {
        console.error('未読通知の監視エラー:', error)
      }
    )

    // メッセージ単位の未読通知リスナー（ポップアップ用）
    const unsubscribeMessages = onSnapshot(
      collection(db, `unreadNotifications/${enterpriseId}/messages`),
      (snapshot) => {
        const messages: UnreadMessage[] = snapshot.docs.map((doc) => {
          const data = doc.data()

          const attachments: AttachmentInfo[] = Array.isArray(data.attachments)
            ? data.attachments.map(
                (attachment: { url: string; fileName: string }) => ({
                  url: attachment.url,
                  fileName: attachment.fileName,
                })
              )
            : []

          return {
            id: doc.id,
            chatRoomId: data.chatRoomId,
            messageId: data.messageId,
            senderEnterpriseId: data.senderEnterpriseId,
            content: data.content,
            attachments,
            createdAt: data.createdAt.toDate(),
            senderName: data.senderName,
            senderLogo: data.senderLogo,
          } satisfies UnreadMessage
        })

        // 作成日時の降順でソート（新しい順）
        messages.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())

        setUnreadMessages(messages)
        setLoading(false)
      },
      (error) => {
        console.error('未読通知の監視エラー:', error)
        setLoading(false)
      }
    )

    // クリーンアップ：コンポーネントがアンマウントされたらリスナーを解除
    return () => {
      unsubscribeChatRooms()
      unsubscribeMessages()
    }
  }, [user?.kuchikomiEnterpriseId])

  return (
    <UnreadNotificationsContext.Provider
      value={{
        unreadCount,
        unreadNotifications,
        unreadMessages,
        loading,
      }}
    >
      {children}
    </UnreadNotificationsContext.Provider>
  )
}

/**
 * 未読通知をリアルタイムで監視するカスタムフック
 * UnreadNotificationsProvider 内で使用する必要があります
 */
export const useUnreadNotifications = (): UnreadNotificationsContextValue => {
  const context = useContext(UnreadNotificationsContext)

  if (!context) {
    throw new Error(
      'useUnreadNotifications must be used within UnreadNotificationsProvider'
    )
  }

  return context
}
